import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'
import { Modal, Form } from 'react-bootstrap'
import { toast } from 'react-toastify'
import ConfirmModal from '../../components/modals/ConfirmModal'

interface SubAdminRecord {
    _id: string;
    name: string;
    email: string;
    role: string;
    permissions: string[];
    createdAt?: string;
}

const AVAILABLE_PERMISSIONS = [
    'Dashboard', 'Manage Users', 'Sub-Admin', 'Site Visibility', 'Lead Settings', 'SEO Management', 'Legal Content',
    'Manage Packages', 'Package Inquiries', 'Inventory', 'Developers', 'Site Visit Bookings', 'CRM Leads',
    'Project Manager', 'Inquiry Portal', 'Lead Dashboard', 'Add Classified Property', 'Saved Properties',
    'Lead Classified Package', 'Lead Package Purchases', 'Cities', 'Localities', 'Locality Highlights',
    'Landmark Categories', 'Price Trends', 'Amenities', 'Specifications', 'Podcasts', 'Reel Management',
    'Testimonials', 'Blog Posts', 'Blog Comments', 'Hero Banners'
];

export default function AdminSubAdmins() {
    const { token, user: currentUser } = useAuth();
    
    const [subAdmins, setSubAdmins] = useState<SubAdminRecord[]>([])
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState<string | null>(null)
    const [showModal, setShowModal] = useState(false)
    const [editingSubAdmin, setEditingSubAdmin] = useState<Partial<SubAdminRecord> | null>(null)
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        permissions: [] as string[]
    })
    const [saving, setSaving] = useState(false)
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: '', name: '' });

    useEffect(() => {
        if (!token) { setLoading(false); return }
        loadData()
    }, [token])

    const loadData = async () => {
        setLoading(true)
        try {
            const data = await api.adminGetSubAdmins(token!)
            if (Array.isArray(data)) setSubAdmins(data)
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    const handleOpenModal = (sub?: SubAdminRecord) => {
        if (sub) {
            setEditingSubAdmin(sub)
            setFormData({
                name: sub.name,
                email: sub.email,
                password: '', // Don't show password
                permissions: sub.permissions || []
            })
        } else {
            setEditingSubAdmin(null)
            setFormData({
                name: '',
                email: '',
                password: '',
                permissions: []
            })
        }
        setShowModal(true)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) return
        setSaving(true)
        try {
            let res;
            if (editingSubAdmin) {
                res = await api.adminUpdateSubAdmin(token, editingSubAdmin._id!, formData)
            } else {
                res = await api.adminCreateSubAdmin(token, formData)
            }

            if (res._id) {
                toast.success(editingSubAdmin ? 'Sub-Admin updated successfully' : 'Sub-Admin created successfully')
                setShowModal(false)
                loadData()
            } else {
                toast.error(res.message || 'Action failed')
            }
        } catch (error) {
            toast.error('Server error')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!token || !confirmModal.id) return
        setDeleting(confirmModal.id)
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
            const res = await api.adminDeleteUser(token, confirmModal.id)
            if (res.message === 'User deleted successfully') {
                setSubAdmins(prev => prev.filter(u => u._id !== confirmModal.id))
                toast.success('Sub-Admin access revoked.')
            } else {
                toast.error(res.message || 'Deletion failed.')
            }
        } catch (error) {
            toast.error('Server error')
        } finally {
            setDeleting(null)
        }
    }

    const togglePermission = (perm: string) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(perm)
                ? prev.permissions.filter(p => p !== perm)
                : [...prev.permissions, perm]
        }))
    }

    return (
        <AdminLayout>
            <div className="animate__animated animate__fadeIn">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                    <div>
                        <h1 className="text-3xl font-bold text-[#05144b] mb-2">Sub-Admin Management</h1>
                        <p className="text-[#05144b] text-[14px] font-semibold">Create and manage administrative accounts with custom permissions.</p>
                    </div>
                    <button 
                        onClick={() => handleOpenModal()}
                        className="inline-flex items-center gap-3 px-8 py-4 bg-[#05144b] text-white font-bold rounded-xl shadow-xl hover:bg-blue-900 transition-all active:scale-95"
                    >
                        <i className="fa-solid fa-user-plus"></i>
                        Add New Sub-Admin
                    </button>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
                    <div className="p-8 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between gap-6 bg-white/50 dark:bg-slate-800/30">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-xl">
                                <i className="fa-solid fa-users-gear text-lg"></i>
                            </div>
                            <div>
                                <h3 className="text-xl md:text-2xl font-bold text-[#05144b] leading-tight">Administrative Staff</h3>
                                <p className="text-[13px] md:text-[14px] font-semibold text-[#05144b]">{subAdmins.length} active sub-admins</p>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-32">
                            <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-6"></div>
                            <p className="text-[#05144b] font-semibold text-[14px]">Synchronizing permissions...</p>
                        </div>
                    ) : subAdmins.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
                            <div className="w-18 h-18 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mb-6 border-2 border-gray-100 dark:border-slate-700 shadow-inner">
                                <i className="fa-solid fa-user-shield text-2xl text-black/10"></i>
                            </div>
                            <h5 className="text-xl font-bold text-black tracking-tight mb-2">No Sub-Admins Found</h5>
                            <p className="text-black/40 text-[13px] font-bold">You haven't delegated any administrative tasks yet.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <tbody className="divide-y divide-slate-100/50 dark:divide-slate-800/50">
                                    {subAdmins.map(sub => (
                                        <tr key={sub._id} className="hover:bg-white/80 dark:hover:bg-slate-800/30 transition-all">
                                            <td className="px-6 py-6 whitespace-nowrap">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-11 h-11 rounded-md bg-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                                        {sub.name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-[#05144b] text-[15px]">{sub.name}</h4>
                                                        <div className="text-[12px] font-medium text-[#05144b]/60 mt-1">{sub.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6">
                                                <div className="flex flex-wrap gap-2 max-w-md">
                                                    {sub.permissions?.slice(0, 3).map((p, i) => (
                                                        <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 text-[10px] font-black rounded-md border border-blue-100">
                                                            {p}
                                                        </span>
                                                    ))}
                                                    {sub.permissions?.length > 3 && (
                                                        <span className="px-3 py-1 bg-gray-50 text-gray-500 text-[10px] font-black rounded-md border border-gray-100">
                                                            +{sub.permissions.length - 3} more
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 text-right">
                                                <div className="flex items-center justify-end gap-3">
                                                    <button
                                                        onClick={() => handleOpenModal(sub)}
                                                        className="w-10 h-10 rounded-md bg-white border border-gray-100 text-black transition-all flex items-center justify-center hover:border-[#05144b] hover:text-[#05144b] shadow-sm"
                                                        title="Edit Permissions"
                                                    >
                                                        <i className="fa-solid fa-user-gear text-sm"></i>
                                                    </button>
                                                    <button
                                                        disabled={deleting === sub._id}
                                                        onClick={() => setConfirmModal({ isOpen: true, id: sub._id, name: sub.name })}
                                                        className="w-10 h-10 rounded-md bg-white border border-gray-100 text-black transition-all flex items-center justify-center hover:border-rose-600 hover:text-rose-600 shadow-sm"
                                                        title="Revoke Access"
                                                    >
                                                        {deleting === sub._id 
                                                            ? <div className="w-5 h-5 border-2 border-rose-600 border-t-transparent rounded-full animate-spin"></div>
                                                            : <i className="fa-solid fa-user-slash text-sm"></i>
                                                        }
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Sub-Admin Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" className="custom-premium-modal">
                <Modal.Body className="p-0">
                    <Form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-md overflow-hidden border border-gray-100 dark:border-slate-800 shadow-2xl">
                        <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/30">
                            <div className="flex items-center gap-4">
                                <div className="w-11 h-11 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-lg">
                                    <i className="fa-solid fa-user-shield text-lg"></i>
                                </div>
                                <div>
                                    <h5 className="text-xl font-bold text-black tracking-tight">{editingSubAdmin ? 'Refine Sub-Admin' : 'New Sub-Admin Profile'}</h5>
                                    <p className="text-[13px] font-bold text-black/60 mt-0.5">Define identity and administrative scope.</p>
                                </div>
                            </div>
                            <button type="button" onClick={() => setShowModal(false)} className="w-9 h-9 rounded-full hover:bg-gray-100 text-black transition-colors flex items-center justify-center">
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>

                        <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto scrollbar-hide">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="text-[13px] font-bold text-black ml-1">Full Name</label>
                                    <input
                                        type="text"
                                        required
                                        className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black outline-none transition-all"
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[13px] font-bold text-black ml-1">Email Address</label>
                                    <input
                                        type="email"
                                        required
                                        className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black outline-none transition-all"
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>
                                {!editingSubAdmin && (
                                    <div className="space-y-1.5 md:col-span-2">
                                        <label className="text-[13px] font-bold text-black ml-1">Access Password</label>
                                        <input
                                            type="password"
                                            required
                                            className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black outline-none transition-all"
                                            value={formData.password}
                                            onChange={e => setFormData({ ...formData, password: e.target.value })}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <label className="text-[13px] font-bold text-black ml-1">Assigned Permissions</label>
                                    <button 
                                        type="button" 
                                        onClick={() => setFormData(prev => ({ 
                                            ...prev, 
                                            permissions: prev.permissions.length === AVAILABLE_PERMISSIONS.length ? [] : [...AVAILABLE_PERMISSIONS] 
                                        }))}
                                        className="text-[11px] font-bold text-[#05144b] hover:underline"
                                    >
                                        {formData.permissions.length === AVAILABLE_PERMISSIONS.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {AVAILABLE_PERMISSIONS.map(perm => (
                                        <div 
                                            key={perm}
                                            onClick={() => togglePermission(perm)}
                                            className={`p-3 rounded-md border-2 cursor-pointer transition-all flex items-center gap-3 ${formData.permissions.includes(perm) ? 'bg-blue-50 border-[#05144b] text-[#05144b]' : 'bg-white border-gray-100 text-gray-400 hover:border-gray-200'}`}
                                        >
                                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.permissions.includes(perm) ? 'border-[#05144b] bg-[#05144b]' : 'border-gray-200'}`}>
                                                {formData.permissions.includes(perm) && <i className="fa-solid fa-check text-[8px] text-white"></i>}
                                            </div>
                                            <span className="text-[11px] font-bold uppercase tracking-tight">{perm}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="p-8 border-t border-gray-100 dark:border-slate-800 flex gap-4 bg-white/50 dark:bg-slate-800/30">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="flex-1 py-4 text-[14px] font-bold text-black hover:text-black/60 transition-colors"
                            >
                                Discard Changes
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex-[2] py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 text-[14px] font-bold rounded-md shadow-lg active:scale-95 transition-all flex items-center justify-center disabled:opacity-50"
                            >
                                {saving ? 'Processing...' : editingSubAdmin ? 'Update Scope' : 'Initialize Sub-Admin'}
                            </button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <ConfirmModal 
                isOpen={confirmModal.isOpen}
                title="Revoke Access"
                message={`Are you sure you want to revoke administrative access for "${confirmModal.name}"? This action is permanent.`}
                onConfirm={handleDelete}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                confirmText="Revoke Now"
                cancelText="Keep Staff"
                type="danger"
            />
        </AdminLayout>
    )
}
